import { observer } from "mobx-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useInviteManagement } from "hooks/state/useInviteManagement";
import { useLocaleDateFormatter } from "hooks/useLocaleDateFormatter";
import { useRedirect } from "hooks/useRedirect";

import hogeschooltaalLogo from "assets/images/logo-hogeschooltaal-green.svg";

import { GENERIC_ROUTES } from "constants/routes";

import { EncircledIcon } from "components/EncircledIcon";
import { FeedbackMessage } from "components/FeedbackMessage";
import { LoadingSpinner } from "components/LoadingSpinner";

import { Heading2 } from "styles/elements/Headings";
import { CssButton, CssFlex } from "styles/helpers/layout";

import {
  ClaimInviteLicenseOutcome,
  ClaimInviteResult,
} from "./components/ClaimInviteResult/ClaimInviteResult";

import {
  StyledContentContainer,
  StyledLicenseInfoBox,
  StyledMetadataRow,
  StyledPageContainer,
} from "./ClaimInviteLinkLicensePage.styled";

export const ClaimInviteLinkLicensePage: React.FC = observer(() => {
  const { t } = useTranslation("homepage");
  const { inviteLinkId } = useParams<{ inviteLinkId: string }>();
  const { redirectToUrl } = useRedirect();
  const { formatDate } = useLocaleDateFormatter();

  const {
    loading: isLoading,
    inviteLinkLicenseInfo: licenseInfo,
    fetchInviteLinkLicenseInfo,
    claimInviteLinkLicense,
  } = useInviteManagement();

  const [activeOutcomeType, setActiveOutcomeType] = useState<string | null>(null);
  const [showInvalidLinkError, setShowInvalidLinkError] = useState(false);

  // Fetch information on the license associated to the invite link id
  useEffect(() => {
    if (inviteLinkId) {
      fetchInviteLinkLicenseInfo(inviteLinkId).then((wasLicenseInfoLoadedSuccessfully) => {
        if (!wasLicenseInfoLoadedSuccessfully) {
          setShowInvalidLinkError(true);
        }
      });
    }
  }, [fetchInviteLinkLicenseInfo, inviteLinkId]);

  const onClaimLicenseButtonClick = () => {
    if (inviteLinkId) {
      claimInviteLinkLicense(inviteLinkId).then(({ success, errCode }) => {
        setActiveOutcomeType(
          success
            ? ClaimInviteLicenseOutcome.Success
            : errCode || ClaimInviteLicenseOutcome.GenericError,
        );
      });
    }
  };

  return (
    <StyledPageContainer>
      <img alt="Hogeschooltaal logo" src={hogeschooltaalLogo} width="200px" />
      <StyledContentContainer data-cy="claim-license-prompt">
        <CssFlex flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
          <EncircledIcon iconName="fas fa-gift" variant="secondary" size="large" />
        </CssFlex>
        <Heading2>{t("homepage:claimInviteLinkLicensePage.title")}</Heading2>
        {isLoading && !licenseInfo && <LoadingSpinner />}
        {/* -- License not found message -- */}
        {showInvalidLinkError && (
          <FeedbackMessage
            dataCy="missing-license-info-message"
            variant="error"
            title={t("homepage:claimInviteLinkLicensePage.licenseNotFoundMessage.title")}
            description={t(
              "homepage:claimInviteLinkLicensePage.licenseNotFoundMessage.description",
            )}
          />
        )}
        {/* -- Info on license associated to link -- */}
        {licenseInfo && (
          <>
            <StyledLicenseInfoBox data-cy="license-info-box">
              <StyledMetadataRow>
                {t("homepage:claimInviteLinkLicensePage.licenseInfo.products")}:{" "}
                <span data-cy="license-products">{licenseInfo.productNames}</span>
              </StyledMetadataRow>
              <StyledMetadataRow>
                {t("homepage:claimInviteLinkLicensePage.licenseInfo.expirationDate")}:{" "}
                <span data-cy="license-expiration-date">
                  {formatDate(licenseInfo.licenseExpirationDate)}
                </span>
              </StyledMetadataRow>
              {licenseInfo.groupName && (
                <StyledMetadataRow>
                  {t("homepage:claimInviteLinkLicensePage.licenseInfo.groupName")}:{" "}
                  <span data-cy="license-group-name">{licenseInfo.groupName}</span>
                </StyledMetadataRow>
              )}
            </StyledLicenseInfoBox>
            {/* -- Show claim button initially -- */}
            {!activeOutcomeType && (
              <CssButton
                dataCy="claim-license-button"
                isLoading={isLoading}
                variant="primary"
                onClick={onClaimLicenseButtonClick}
              >
                {t("homepage:claimInviteLinkLicensePage.actionButtons.claim")}
              </CssButton>
            )}
            {/* -- Show outcome of claim operation -- */}
            {activeOutcomeType && (
              <>
                <ClaimInviteResult outcomeType={activeOutcomeType} licenseInfo={licenseInfo} />
                <CssButton
                  dataCy="return-home-button"
                  variant="primary"
                  onClick={() => redirectToUrl(GENERIC_ROUTES.HOME_PAGE)}
                >
                  {t("homepage:claimInviteLinkLicensePage.actionButtons.return")}
                </CssButton>
              </>
            )}
          </>
        )}
      </StyledContentContainer>
    </StyledPageContainer>
  );
});
